import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProducts, getMakes, getAllMotorcycleModels } from '../api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Filter, X } from 'lucide-react';

const PART_TYPES = [
  'Engine & Drivetrain',
  'Bodywork',
  'Electrical',
  'Suspension & Wheels',
  'Apparel',
  'Accessories'
];

const CONDITIONS = ['New', 'Used – Normal Use', 'Used – Fair', 'NOS'];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [makes, setMakes] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => ({
    partType: searchParams.get('partType') || '',
    condition: searchParams.get('condition') || '',
    make: searchParams.get('make') || '',
    model: searchParams.get('model') || '',
    year: searchParams.get('year') || '',
    search: searchParams.get('search') || '',
    page: searchParams.get('page') || 1,
    hotDeal: searchParams.get('hotDeal') || '',
  }), [searchParams]);

  useEffect(() => {
    getMakes().then(setMakes);
    getAllMotorcycleModels().then(setAllModels);
  }, []);

  useEffect(() => {
    if (filters.make) {
      setFilteredModels(allModels.filter(m => m.make === filters.make));
    } else {
      setFilteredModels(allModels);
    }
  }, [filters.make, allModels]);

  useEffect(() => {
    // Clear model when make is cleared
    if (!filters.model && filters.make) return;
    if (filters.model && !filters.make) {
      // model still valid when no make
    }
  }, [filters.model, filters.make]);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== '')
    );
    getProducts(params)
      .then(data => { setProducts(data.products); setPagination(data.pagination); })
      .catch(err => console.error('Failed to fetch products:', err))
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key, value) => {
    setSearchParams(f => {
      if (value === '') {
        f.delete(key);
      } else {
        f.set(key, value);
      }
      if (key === 'page') f.set('page', value);
      return f;
    });
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const formatPrice = (price) =>
    price?.toLocaleString('en-CA', { minimumFractionDigits: 2, style: 'currency', currency: 'CAD' });

  const conditionColors = {
    'New': 'bg-foreground text-background',
    'NOS': 'bg-brand-yellow text-foreground',
    'Used – Normal Use': 'bg-muted text-foreground',
    'Used – Fair': 'bg-brand-red text-background',
  };

  const hasActiveFilters = filters.partType || filters.condition || filters.make || filters.model || filters.year || filters.search;

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-8">
      {/* Page Header */}
      <div className="bg-muted/30 border-b border-border">
        <div className="container px-4 py-6 md:py-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            Shop
          </h1>
          <p className="text-sm text-muted-foreground">
            Find the parts you need
          </p>
        </div>
      </div>

      <div className="container px-4 py-6 md:py-8">
        {/* Filters - Horizontal Layout like Motorcycles page */}
        <div className="mb-6 p-4 border border-border rounded-lg bg-background">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-foreground" />
              <h2 className="font-semibold text-foreground">Filters</h2>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 text-xs">
                Clear All
              </Button>
            )}
          </div>

          <Separator className="mb-4" />

          {/* Filter Inputs - Horizontal on desktop, wrapped on mobile */}
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-1 block">
                Search
              </label>
              <Input
                placeholder="Part, brand..."
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
                className="h-9"
              />
            </div>

            {/* Part Type */}
            <div className="min-w-[150px]">
              <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-1 block">
                Part Type
              </label>
              <Select value={filters.partType} onValueChange={(val) => setFilter('partType', val)}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Types</SelectItem>
                  {PART_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Condition */}
            <div className="min-w-[150px]">
              <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-1 block">
                Condition
              </label>
              <Select value={filters.condition} onValueChange={(val) => setFilter('condition', val)}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="All Conditions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Conditions</SelectItem>
                  {CONDITIONS.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Make */}
            <div className="min-w-[150px]">
              <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-1 block">
                Make
              </label>
              <Select value={filters.make} onValueChange={(val) => {
                setFilter('make', val);
                if (val === '') {
                  setFilter('model', '');
                }
              }}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="All Makes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Makes</SelectItem>
                  {makes.map(m => (
                    <SelectItem key={m._id} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Model */}
            <div className="min-w-[150px]">
              <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-1 block">
                Model
              </label>
              <Select value={filters.model} onValueChange={(val) => setFilter('model', val)}>
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="All Models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Models</SelectItem>
                  {filteredModels.map(m => (
                    <SelectItem key={m._id} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Year */}
            <div className="min-w-[120px]">
              <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-1 block">
                Year
              </label>
              <Input
                placeholder="e.g. 2003"
                type="number"
                value={filters.year}
                onChange={(e) => setFilter('year', e.target.value)}
                min="1970"
                max={new Date().getFullYear()}
                className="h-9"
              />
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <>
              <Separator className="my-4" />
              <div>
                <label className="text-xs font-medium text-foreground uppercase tracking-wider mb-2 block">
                  Active Filters
                </label>
                <div className="flex flex-wrap gap-1">
                  {filters.partType && (
                    <Badge variant="secondary" className="text-xs px-2 py-1">
                      {filters.partType}
                      <button onClick={() => setFilter('partType', '')} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.condition && (
                    <Badge variant="secondary" className="text-xs px-2 py-1">
                      {filters.condition}
                      <button onClick={() => setFilter('condition', '')} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.make && (
                    <Badge variant="secondary" className="text-xs px-2 py-1">
                      {filters.make}
                      <button onClick={() => setFilter('make', '')} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.model && (
                    <Badge variant="secondary" className="text-xs px-2 py-1">
                      {filters.model}
                      <button onClick={() => setFilter('model', '')} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.year && (
                    <Badge variant="secondary" className="text-xs px-2 py-1">
                      {filters.year}
                      <button onClick={() => setFilter('year', '')} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-muted-foreground">
            {loading ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <>
                <span className="font-semibold text-foreground">{pagination.total || 0}</span> results
              </>
            )}
          </span>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <CardContent className="p-4 space-y-2">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-5 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-4">No products found.</p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                conditionColors={conditionColors}
                formatPrice={formatPrice}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && !loading && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilter('page', Math.max(1, parseInt(filters.page) - 1))}
              disabled={parseInt(filters.page) === 1}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                let pageNum = i + 1;
                if (pagination.pages > 5) {
                  const offset = Math.max(0, parseInt(filters.page) - 3);
                  pageNum = i + offset;
                }
                return (
                  <Button
                    key={pageNum}
                    variant="outline"
                    size="sm"
                    onClick={() => setFilter('page', pageNum)}
                    className={`w-9 h-9 p-0 ${
                      filters.page === pageNum.toString()
                        ? 'bg-foreground text-background border-foreground'
                        : ''
                    }`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilter('page', Math.min(pagination.pages, parseInt(filters.page) + 1))}
              disabled={parseInt(filters.page) === pagination.pages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ product, conditionColors, formatPrice }) {
  return (
    <Card className="overflow-hidden border-border hover:shadow-lg transition-shadow duration-200 group">
      <a href={`/product/${product._id}`} className="block">
        {/* Image */}
        <div className="relative bg-muted/50 aspect-[4/3] overflow-hidden">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              No Image
            </div>
          )}
          <Badge className={`absolute top-2 left-2 ${conditionColors[product.condition] || 'bg-muted'}`}>
            {product.condition}
          </Badge>
        </div>

        {/* Content */}
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            {product.partCategory}
          </p>
          <h3 className="text-sm font-semibold text-foreground line-clamp-2 mb-2 leading-tight group-hover:text-brand-red transition-colors">
            {product.title}
          </h3>
          {product.fitment?.[0] && (
            <p className="text-xs text-muted-foreground mb-2">
              {product.fitment[0].yearFrom}–{product.fitment[0].yearTo} {product.fitment[0].make}
            </p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-lg font-bold text-foreground">
              {formatPrice(product.price)}
            </p>
            {product.compareAtPrice && (
              <p className="text-xs text-muted-foreground line-through">
                {formatPrice(product.compareAtPrice)}
              </p>
            )}
          </div>
        </CardContent>
      </a>
    </Card>
  );
}